import PropTypes from 'prop-types';
import React from 'react';
import classnames from 'classnames';

import useCurrentUser from 'dpl/common/hooks/useCurrentUser';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_VIEW_FEATURES
} from 'dpl/constants/analytics';
import { sendRudderstackEvent } from 'dpl/util/analytics';
import { get } from 'dpl/shared/utils/object';
import { setAnalyticsOrigin } from 'dpl/shared/utils/setAnalyticsOrigin';
import { BUYER_MESSAGES_PATH } from 'dpl/shared/constants/urls';
import { openPuppyPage } from 'dpl/puppy_page/utils/helpers';
import { PUPPY_CARD_CTA_TYPES } from 'dpl/puppy_search_results/utils/constants';

import Transport from './Transport';

const INITIAL_INQUIRIES_FIELD_NAME =
  'value.data.breeder_profiles_initial_inquiries';

export default function CardFooter({
  breederProfileUrl,
  isReservedPuppy,
  snoozed,
  deliveryMethods,
  breederProfileId,
  analytics,
  puppyId,
  litterId,
  breederFirstName,
  facilityLocation,
  puppyName,
  distance,
  ctaType
}) {
  const currentUser = useCurrentUser();

  function handleApplyClick(e) {
    e.preventDefault();
    e.stopPropagation();

    setAnalyticsOrigin({
      originViewFeature: analytics?.view_feature,
      originViewFeatureLocation: analytics?.view_feature_location
    });

    const initialInquiries = get(currentUser, INITIAL_INQUIRIES_FIELD_NAME, []);

    const initialInquiry = initialInquiries.find(
      inquiry => inquiry.breeder_profile_id === breederProfileId
    );

    if (initialInquiry) {
      window.location.href = `${BUYER_MESSAGES_PATH}/active/${
        initialInquiry.conversation_id || ''
      }`;
    } else {
      const view = analytics?.view ?? '';
      window.location.href = `/puppy/${puppyId}/contacts/buyer_application?view=${view}`;
    }
  }

  function handleViewInfoClick(e) {
    e.preventDefault();
    e.stopPropagation();

    sendRudderstackEvent(ANALYTICS_EVENTS.CARD_CLICKED_BY_USER, {
      ...analytics,
      litter_id: litterId,
      puppy_id: puppyId,
      view_feature: ANALYTICS_VIEW_FEATURES.VIEW_DETAILS_BUTTON
    });

    if (puppyId) {
      openPuppyPage({ puppyId });
    } else {
      window.location.href = breederProfileUrl;
    }
  }

  return (
    <div
      className={classnames(
        'PuppySearchResultsCardFooter items-center f2 mt1',
        {
          'bt b--light-gray': !puppyId
        }
      )}
    >
      <div>
        {!snoozed && (
          <Transport
            deliveryMethods={deliveryMethods}
            breederFirstName={breederFirstName}
            facilityLocation={facilityLocation}
            puppyName={puppyName}
            distance={distance}
          />
        )}
      </div>
      {!snoozed && ctaType !== PUPPY_CARD_CTA_TYPES.NONE && (
        <div className="mt1">
          <CardCTA
            isReservedPuppy={isReservedPuppy}
            handleViewInfoClick={handleViewInfoClick}
            handleApplyClick={handleApplyClick}
            ctaType={ctaType}
          />
        </div>
      )}
    </div>
  );
}

CardFooter.propTypes = {
  breederProfileUrl: PropTypes.string.isRequired,
  snoozed: PropTypes.bool.isRequired,
  isReservedPuppy: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  deliveryMethods: PropTypes.object,
  breederProfileId: PropTypes.number.isRequired,
  analytics: PropTypes.shape({
    view: PropTypes.string.isRequired,
    view_feature: PropTypes.string,
    view_feature_location: PropTypes.string
  }),
  puppyId: PropTypes.number,
  litterId: PropTypes.number,
  breederFirstName: PropTypes.string,
  facilityLocation: PropTypes.string,
  puppyName: PropTypes.string,
  distance: PropTypes.number,
  ctaType: PropTypes.oneOf(Object.values(PUPPY_CARD_CTA_TYPES))
};

CardFooter.defaultProps = {
  deliveryMethods: {},
  analytics: {},
  puppyId: null,
  litterId: null,
  isReservedPuppy: false,
  breederFirstName: null,
  facilityLocation: null,
  puppyName: null,
  distance: null,
  ctaType: PUPPY_CARD_CTA_TYPES.LINK
};

function CardCTA({
  isReservedPuppy,
  handleViewInfoClick,
  handleApplyClick,
  ctaType
}) {
  if (isReservedPuppy) {
    return (
      <button
        type="button"
        data-test-id="breeder_card_view_breeder_link"
        className="box-shadow-hover bg-near-white b--light-gray ba br3 pv2 ph4 fw-medium nowrap"
        onClick={handleViewInfoClick}
      >
        View details
      </button>
    );
  }

  if (ctaType === PUPPY_CARD_CTA_TYPES.BUTTON) {
    return (
      <button
        type="button"
        data-test-id="breeder_card_apply_button"
        className="box-shadow-hover bg-near-white br1 mt2 pv2 ph4 fw-medium nowrap midnight w-100 tc"
        onClick={handleViewInfoClick}
      >
        View details
      </button>
    );
  }

  return (
    <button
      type="button"
      data-test-id="breeder_card_apply_button"
      className="pt1 font-14 underline"
      onClick={handleApplyClick}
    >
      Request info
    </button>
  );
}

CardCTA.propTypes = {
  isReservedPuppy: PropTypes.bool.isRequired,
  handleViewInfoClick: PropTypes.func.isRequired,
  handleApplyClick: PropTypes.func.isRequired,
  ctaType: PropTypes.oneOf(Object.values(PUPPY_CARD_CTA_TYPES))
};

CardCTA.defaultProps = {
  ctaType: PUPPY_CARD_CTA_TYPES.LINK
};
